/**
 * Which art theme this page draws: `v2`, the painted set under `public/art-v2/`, or `v1`, the
 * vector art the site shipped with.
 *
 * WHY A MODULE SINGLETON READ FROM THE URL. The choice has to be legible to code that is nowhere
 * near React — `Atlas` and `scene.ts` resolve textures long before and long after any component
 * renders — and it has to be settled before the first frame is asked for, because a texture swap
 * halfway through a scene would leave half a courtyard painted. Reading it once at module load,
 * the way `sceneOptions` does for `?freeze=`, gives both properties for nothing.
 *
 * v2 is what the site looks like (F40). v1 is not dead: it is still the art behind every frame
 * `V2_FRAMES` does not carry — the sun, the clouds, the birds, the crack decals, the lit windows —
 * so the atlas ships either way, and `?art=v1` is how to see the whole vector courtyard again.
 *
 * **The query parameter is the only input, per request.** Nothing is remembered, and that is the
 * owner's decision rather than a simplification that fell out of the code: everybody sees the
 * painted art unless the URL being loaded says otherwise. Two consequences are worth being plain
 * about, because both are intended and both look like bugs from the outside:
 *
 *   - Somebody who chose the vector theme before F40 is moved to the painted one on their next
 *     visit, silently. Their old choice is gone; `?art=v1` is how they get back to it.
 *   - `?art=v1` does not survive a click. Follow a link off that page and the next one is painted
 *     again, because the parameter is not on it. Reaching the vector art twice means asking twice.
 *
 * There is still deliberately no control for this anywhere in the UI: the owner's decision is that
 * the *choice* stays unadvertised, so the URL is the only way to the other theme and nobody finds
 * it by clicking around.
 */
import { V2_EXT, V2_FRAMES } from './v2Frames'

export type ArtTheme = 'v1' | 'v2'

/**
 * What a visitor who has not asked for the vector art sees — which is everybody else. One line,
 * and the whole of F40's user-visible change; putting the old art back is the same line.
 */
const DEFAULT_THEME: ArtTheme = 'v2'

/**
 * The theme for this page load, from this page's own URL and nothing else.
 *
 * `?art=v1` is the only thing that turns the painted art off. `?art=v2` is accepted because pages
 * link to it (`AdsReturn`, `AdsCancel`) and because a reader of a URL should not have to know which
 * value happens to be the default, but it asks for what it would have got anyway.
 *
 * This used to read and write `localStorage['mha.art']` so a choice survived a click. The owner has
 * removed that: the painted theme overrides every earlier choice, so a stored value would only be a
 * way for one to come back. With nothing left reading the key, writing it would be dead code and
 * the `try`/`catch` that guarded a private window would be protecting nothing, so all three are
 * gone. An `mha.art` left in a returning visitor's browser by an older build is inert.
 */
function initial(): ArtTheme {
  if (typeof window === 'undefined') return DEFAULT_THEME
  return new URLSearchParams(window.location.search).get('art') === 'v1' ? 'v1' : DEFAULT_THEME
}

let theme: ArtTheme = initial()

export function artTheme(): ArtTheme {
  return theme
}

/** For the Lab and tests, which have no query string of their own. */
export function setArtTheme(next: ArtTheme): void {
  theme = next
}

/**
 * The painted texture for a frame, or `null` to leave it to v1.
 *
 * `null` is not an error: several frames are v1 on purpose (`scripts/gen-art-v2.mts` § jobs says
 * which and why — the sun and clouds are the pale backdrop the painted objects sit against, and a
 * painted `window_light` would draw a second window over the building's own). But now that v2 is
 * what everybody sees, this fallback is also how a *missing* frame would hide: the courtyard comes
 * up whole, one prop in vector, and nothing anywhere says so. Compare `V2_FRAMES` against what the
 * scene asks for before assuming a flat-looking prop is deliberate.
 *
 * Served straight out of `public/` rather than packed into the atlas, which is a real cost now that
 * it is the default path. Measured on the real host (F40): a cold `/` fetches **121 frames /
 * 1.33 MB**, putting the whole page at 1.88 MB against 545 kB for `?art=v1`. The
 * `total-byte-weight` gate is 2.5 MB and is an `error` in `lighthouserc.json`, so there is about
 * 620 kB of room and a repaint that doubles a sky would spend it. As palettised PNG the same 121
 * frames were 3.76 MB and the page was 4.35 MB, which failed that gate outright — the frames are
 * WebP at quality 85 for that reason, and `scripts/painted-art.mts` is where the codec is chosen
 * and defended.
 *
 * They are unhashed, so `PublicSurface.Policy` gives `/art-v2/` an hour rather than the `immutable`
 * year it gives the two hashed directories, and they are in no service-worker cache either
 * (`src/sw/sw.template.js` intercepts `/assets/` and `/atlas/`). What serving them raw buys is that
 * a repainted frame ships on the next reload with no build step; packing them into the atlas would
 * answer every line of this paragraph at once.
 */
export function paintedUrl(frameName: string): string | null {
  if (theme !== 'v2' || !V2_FRAMES.has(frameName)) return null
  return paintedAssetUrl(frameName)
}

/**
 * Where a painted frame lives, whatever theme this page is drawing.
 *
 * The advertising dialog and the manage page draw the real board in an `<img>` (F37) and are
 * reachable from a v1 page, so they cannot go through {@link paintedUrl} — but the path is still
 * one fact, and this is where it is written.
 *
 * The extension comes from the generated file beside the frame list rather than being spelled here,
 * because the script that wrote the files is the only thing that knows what format they are in.
 * Spelling it twice is how a re-encode ships a directory of WebP and a page asking for PNG — 227
 * 404s, every prop silently back in vector art, and a green build.
 */
export function paintedAssetUrl(frameName: string): string {
  return `/art-v2/${frameName}.${V2_EXT}`
}
